import { createApp } from 'vue';
import App from './App.vue';
// plugins
import amplify from './config/amplify';
import router from './config/router';
import vuetify from './config/vuetify';
import { setupCalendar, DatePicker } from 'v-calendar';
// style
import 'v-calendar/style.css';
import '@/assets/stylesheets/index.scss';
import 'vuetify/styles';
// global state
import { keyCurrentUser, createCurrentUser } from '@/models/global/currentUser';
import {
  keyCurrentHeaderFilter,
  createCurrentHeaderFilter,
} from '@/models/global/currentHeaderFilter';
import {
  keyCurrentOrganization,
  createCurrentOrganization,
} from '@/models/global/currentOrganization';
import {
  getCurrentOrganizationLabel,
  getCurrentTenantId,
} from '@/models/global/currentOrganization';
import { keyFormErrors, createFormErrors } from '@/models/global/formErrors';
import { keyHtmlHead, createHtmlHead } from '@/models/global/htmlHead';
import timezone from '@/models/global/timezone';

// sentry
import * as Sentry from '@sentry/vue';
import { isGtagLoadErrorEvent } from '@/utils/sentry/isGtagLoadErrorEvent';
import { useSettings } from '@/config/settings';
// Google Analytics 4
import { setupGoogleAnalytics } from '@/config/googleAnalytics';

const app = createApp(App);

// plugins
amplify.initialize();
app.use(router);
app.use(vuetify);
app.use(setupCalendar, {
  locales: { ja: { masks: { L: 'YYYY-MM-DD', title: 'YYYY年M月' } } },
});
app.component('DatePicker', DatePicker);

// global state
app.provide(keyCurrentUser, createCurrentUser());
app.provide(keyCurrentHeaderFilter, createCurrentHeaderFilter());
app.provide(keyCurrentOrganization, createCurrentOrganization());
app.provide(keyFormErrors, createFormErrors());
app.provide(keyHtmlHead, createHtmlHead());
app.use(timezone);

// sentry
const SENTRY_DSN =
  'https://db93037f06224f0580a6fd6f8f5b3686@o1172945.ingest.sentry.io/4504132874469376';
const { settings } = useSettings();
if (settings.sentry.enabled) {
  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    environment: settings.env,
    release: settings.commitId || undefined,
    ignoreErrors: [
      // デプロイ起因の動的インポートエラーはアプリのバグではないためSentryへの報告を抑制する
      // Chrome系: "Failed to fetch dynamically imported module"
      // Firefox: "error loading dynamically imported module"
      // Safari: "Importing a module script failed"
      'Failed to fetch dynamically imported module',
      'error loading dynamically imported module',
      'Importing a module script failed',
      'Unable to preload CSS for',
    ],
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      // セッションリプレイはproduction環境でのみ有効化
      ...(settings.env === 'production'
        ? [
            Sentry.replayIntegration({
              // プライバシー処理設定: https://docs.sentry.io/platforms/javascript/session-replay/privacy/
              maskAllText: true,
              blockAllMedia: false,
              // UIで使われるsvg画像はブロックしない
              block: [
                'img',
                'video',
                'object',
                'picture',
                'embed',
                'map',
                'audio',
              ],
              // ヘッダーとサイドメニューはマスクしない
              unmask: ['.p-global-layout__menu, .p-global-layout__header'],
            }),
          ]
        : []),
    ],
    ...(settings.env === 'production' && {
      // エラーが発生したセッションのみリプレイを記録する
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
    }),
    tracePropagationTargets: [settings.env, settings.host, /^\//],
    tracesSampleRate: 1.0,
    // GAのgtag.jsのロード失敗は生のDOM error Eventで届き、文字列ベースのignoreErrorsでは
    // 落とせないためここで抑制する(SREST-FRONTEND-XH)。他のエラーの可視性は保つ。
    beforeSend(event, hint) {
      if (isGtagLoadErrorEvent(hint?.originalException)) return null;
      try {
        const connection = (navigator as any)?.connection;
        event.tags = {
          ...event.tags,
          online: String(navigator.onLine),
          effective_type: connection?.effectiveType,
          visibility: document.visibilityState,
        };
        event.contexts = {
          ...event.contexts,
          client_state: {
            online: navigator.onLine,
            effective_type: connection?.effectiveType,
            rtt: connection?.rtt,
            downlink: connection?.downlink,
            device_memory: (navigator as any)?.deviceMemory,
          },
        };
      } catch {
        // エンリッチに失敗しても送信は継続する
      }
      return event;
    },
  });
}

// Google Analytics 4
setupGoogleAnalytics(
  app,
  router,
  getCurrentOrganizationLabel,
  getCurrentTenantId
);

// デプロイ後にチャンク(module script)の読み込みが失敗した場合、最新を取得するため
// リロードする。message ベースの router.onError では拾えない生の error Event 経路を補う。
// サーバー障害/オフライン時の無限リロードを防ぐため直近リロードから一定時間は抑止する。
window.addEventListener('vite:preloadError', () => {
  const key = 'vite-preload-reload-at';
  const last = Number(sessionStorage.getItem(key) || 0);
  // 10秒のクールダウンでループ防止。恒久障害/オフライン時は再試行に任せる
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(key, String(Date.now()));
  window.location.reload();
});

app.mount('#app');
